import type { ReactNode } from 'react';
import { cx } from '../cx';
import type { ChartLayout } from './layout';
import { CHART_MINIMUM, TICK_TEXT, niceScale, percentOf, thinTick } from './scale';
import { sampleTier, type ThinLabels } from './thin';

/** One ad: its id and its monthly pay. */
export type PayDot = { id: string; value: number };

export type PayDotsLayout = {
  /** The plotted scale; `max` is exactly `min + bins.length * step`, so bins line up with it. */
  domain: { min: number; max: number };
  /** The pay each bin covers (a round amount: €100, €250, €500, ...). */
  step: number;
  bins: Array<{ start: number; dots: PayDot[] }>;
  ticks: number[];
  /** Median and middle half over every ad, the ones off the scale included. */
  stats: { n: number; median: number; p25: number; p75: number } | null;
  /** Ads far below or above the rest (outside the IQR fences), counted at the edges, not drawn. */
  below: PayDot[];
  above: PayDot[];
};

const STEPS = [50, 100, 200, 250, 300, 400, 500, 1000, 2000, 2500, 5000, 10000];

function quantile(sorted: number[], p: number): number {
  if (sorted.length === 1) return sorted[0];
  const i = (sorted.length - 1) * p;
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
}

/**
 * Lays out pay dots: stats over every ad, the scale clipped to Tukey's fences (p25 - 1.5 IQR to
 * p75 + 1.5 IQR, never wider than the data) so a few implausible ads do not crush the rest, and
 * round-amount bins (about `targetBins` of them) that the dots stack in. Pages use `domain` to turn
 * a pointer position back into pay (for a brush).
 */
export function payDotsLayout(dots: PayDot[], targetBins = 60): PayDotsLayout {
  const sorted = dots.map((d) => d.value).sort((a, b) => a - b);
  if (!sorted.length)
    return { domain: { min: 0, max: 1 }, step: 1, bins: [], ticks: [], stats: null, below: [], above: [] };
  const p25 = quantile(sorted, 0.25);
  const p75 = quantile(sorted, 0.75);
  const iqr = p75 - p25;
  const low = Math.max(sorted[0], p25 - 1.5 * iqr);
  const high = Math.min(sorted[sorted.length - 1], p75 + 1.5 * iqr);
  const scale = niceScale(Math.max(0, low), Math.max(high, low + 1));
  const span = scale.max - scale.min;
  const step = STEPS.find((s) => span / s <= targetBins) ?? STEPS[STEPS.length - 1];
  const count = Math.max(1, Math.ceil(span / step));
  const min = Math.floor(scale.min / step) * step;
  const max = min + count * step;
  const inside = dots.filter((d) => d.value >= min && d.value <= max).sort((a, b) => a.value - b.value);
  const bins = Array.from({ length: count }, (_, i) => ({ start: min + i * step, dots: [] as PayDot[] }));
  for (const d of inside) bins[Math.min(count - 1, Math.floor((d.value - min) / step))].dots.push(d);
  return {
    domain: { min, max },
    step,
    bins,
    ticks: scale.ticks.filter((t) => t >= min && t <= max),
    stats: { n: sorted.length, median: quantile(sorted, 0.5), p25, p75 },
    below: dots.filter((d) => d.value < min),
    above: dots.filter((d) => d.value > max),
  };
}

export type PayDotsProps = {
  /** One per ad that states pay. Under 10 the chart says too few; under 20 it draws no middle half. */
  dots: PayDot[];
  /** The ad the reader is pointing at in the list: sun with an ink ring. */
  highlightId?: string;
  /** The reader's selected pay range (a brush): dots inside stay ink, the rest fade. */
  selection?: { lo: number; hi: number };
  /** The median, formatted with its words ("Median €6,300"). */
  medianLabel: ReactNode;
  /** Names the shaded band ("Middle half of ads"). */
  bandLabel: ReactNode;
  /** "12 over €18k", "3 under €2k": how ads off the scale are counted. */
  edgeLabel: (count: number, edge: number, side: 'below' | 'above') => ReactNode;
  formatTick: (value: number) => string;
  thinLabels: ThinLabels;
  /** How many dots a bin stacks before the rest become "+n" (default 16). */
  maxRows?: number;
  /** About how many bins across (default 60). Pass the same to `payDotsLayout` when brushing. */
  targetBins?: number;
  /** `auto` (default) switches to the narrow form in a narrow container: smaller dots and fewer ticks. */
  layout?: ChartLayout;
  /** One sentence with the median, the middle half and how many ads, for screen readers. */
  description: string;
  className?: string;
};

const DOT: Record<ChartLayout, string> = { auto: 'max-w-2 @md:max-w-2.5', narrow: 'max-w-2', wide: 'max-w-2.5' };

/**
 * Every ad that states pay as one dot, stacked by pay: where a search's pay sits, how wide it
 * spreads, and where the reader's pick falls. The middle half is shaded and the median is a dashed
 * reference line with its figure. Dots are all one neutral colour (colour means something here):
 * the ad the reader points at is sun, a selection keeps its dots in ink. Ads far off the scale are
 * counted at the edges rather than stretching it. Renders without JavaScript; a page adds the brush
 * by turning a pointer position into pay with `payDotsLayout(...).domain`.
 */
export function PayDots({
  dots,
  highlightId,
  selection,
  medianLabel,
  bandLabel,
  edgeLabel,
  formatTick,
  thinLabels,
  maxRows = 16,
  targetBins = 60,
  layout = 'auto',
  description,
  className,
}: PayDotsProps) {
  const tier = sampleTier(dots.length);
  if (tier === 'none') return <p className="type-meta text-jc-ink-secondary">{thinLabels.tooFew}</p>;
  const plot = payDotsLayout(dots, targetBins);
  const { domain, stats } = plot;
  const pos = (v: number) => percentOf(v, domain.min, domain.max);
  const showBand = stats && dots.length >= CHART_MINIMUM.spread;
  const inSelection = (v: number) => !selection || (v >= selection.lo && v <= selection.hi);
  const medianPos = stats ? pos(stats.median) : 0;
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="relative min-h-24 pt-7" aria-hidden="true">
        {showBand ? (
          <span
            className="absolute inset-y-0 rounded-jc-info bg-jc-elevated"
            style={{ left: `${pos(stats.p25)}%`, width: `${pos(stats.p75) - pos(stats.p25)}%` }}
          ></span>
        ) : null}
        {selection ? (
          <span
            className="absolute inset-y-0 rounded-jc-info bg-jc-ink/5 ring-2 ring-jc-ink ring-inset"
            style={{ left: `${pos(selection.lo)}%`, width: `${Math.max(0, pos(selection.hi) - pos(selection.lo))}%` }}
          />
        ) : null}
        {stats ? (
          <span
            className="absolute inset-y-0 z-10 border-l-2 border-dashed border-jc-ink-secondary"
            style={{ left: `${medianPos}%` }}
          >
            <span
              className={cx(
                'absolute top-0 text-xs font-semibold whitespace-nowrap text-jc-ink',
                medianPos > 60 ? 'right-1.5 text-right' : 'left-1.5',
              )}
            >
              {medianLabel}
              {tier === 'rough' ? <>, {thinLabels.rough}</> : null}
            </span>
          </span>
        ) : null}
        <div className="relative flex items-end gap-px pb-5">
          {plot.bins.map((b) => {
            const over = b.dots.length > maxRows ? b.dots.length - (maxRows - 1) : 0;
            const shown = over ? b.dots.slice(0, maxRows - 1) : b.dots;
            const hasHighlight = over > 0 && b.dots.slice(maxRows - 1).some((d) => d.id === highlightId);
            return (
              <div key={b.start} className="flex min-w-0 flex-1 flex-col-reverse items-center gap-px">
                {shown.map((d) => (
                  <span
                    key={d.id}
                    className={cx(
                      'block aspect-square w-full rounded-full',
                      DOT[layout],
                      d.id === highlightId
                        ? 'relative z-20 scale-150 bg-jc-chart-2 ring-2 ring-jc-ink'
                        : selection && inSelection(d.value)
                          ? 'bg-jc-ink'
                          : 'bg-jc-chart-context',
                      !inSelection(d.value) && d.id !== highlightId && 'opacity-30',
                    )}
                  />
                ))}
                {over ? (
                  <span
                    className={cx(
                      'text-xs leading-none font-semibold tabular-nums',
                      hasHighlight ? 'text-jc-ink' : 'text-jc-ink-secondary',
                    )}
                  >
                    +{over}
                  </span>
                ) : null}
              </div>
            );
          })}
          <span className="absolute inset-x-0 bottom-5 border-t border-jc-chart-grid" />
        </div>
      </div>
      <div className="relative h-4" aria-hidden="true">
        {plot.ticks.map((t, i) => (
          <span
            key={t}
            className={cx(
              'absolute whitespace-nowrap',
              TICK_TEXT,
              i === 0 && pos(t) < 5 ? '' : pos(t) > 95 ? '-translate-x-full' : '-translate-x-1/2',
              thinTick(i, plot.ticks.length),
            )}
            style={{ left: `${pos(t)}%` }}
          >
            {formatTick(t)}
          </span>
        ))}
      </div>
      {/* The key: what the shading is, and the ads off the scale, counted. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 type-meta text-jc-ink-secondary">
        {showBand ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-5 rounded-jc-info bg-jc-elevated" aria-hidden="true" />
            {bandLabel}
          </span>
        ) : null}
        {plot.below.length ? <span>‹ {edgeLabel(plot.below.length, domain.min, 'below')}</span> : null}
        {plot.above.length ? <span>{edgeLabel(plot.above.length, domain.max, 'above')} ›</span> : null}
      </div>
    </div>
  );
}
